// ============================================
// USER DETAIL PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Mail, Calendar, Shield, User, Loader2 } from 'lucide-react';
import { PageHeader, StatusBadge } from '@/components';
import { adminApi } from '@/services/api';
import { formatDateTime } from '@/utils';

export const Route = createFileRoute('/_authenticated/users/$id/')({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: userData, isLoading } = useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => adminApi.getUser(id),
  });

  // Handle both direct and wrapped response formats
  const user = (userData as any)?.data || userData;

  // Durum değiştir mutation
  const statusMutation = useMutation({
    mutationFn: (data: { status: string; reason?: string }) =>
      adminApi.updateUserStatus(id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', id] });
    },
  });

  // Email doğrulama toggle mutation
  const emailVerifyMutation = useMutation({
    mutationFn: (emailVerified: boolean) => adminApi.updateUser(id, { emailVerified }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Kullanıcı bulunamadı</p>
        <Link to="/users" className="text-maritime-600 hover:underline mt-2 inline-block">
          Kullanıcılara Dön
        </Link>
      </div>
    );
  }

  const roleLabels: Record<string, string> = {
    SEAFARER: 'Denizci',
    COMPANY: 'Şirket',
    ADMIN: 'Yönetici',
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kullanıcı Detayı"
        description={user.email}
        actions={
          <div className="flex gap-2">
            <Link
              to="/users"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </Link>
            <Link
              to={`/users/${id}/edit` as any}
              className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700"
            >
              <Edit className="h-4 w-4" />
              Düzenle
            </Link>
          </div>
        }
      />

      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Mail className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">E-posta</p>
                <p className="font-medium">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <User className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rol</p>
                <p className="font-medium">{roleLabels[user.role] || user.role}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Shield className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Durum</p>
                <StatusBadge status={user.status} />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Calendar className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                <p className="font-medium">{formatDateTime(user.createdAt)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Calendar className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Son Giriş</p>
                <p className="font-medium">{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Mail className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">E-posta Doğrulama</p>
                <p className="font-medium">{user.emailVerified ? 'Doğrulandı' : 'Bekliyor'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Aksiyonları */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Admin Aksiyonları</h3>
        <div className="flex flex-wrap items-center gap-4">
          {/* Durum Değiştir */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hesap Durumu</label>
            <div className="flex items-center gap-2">
              <select
                value={user.status}
                onChange={(e) => statusMutation.mutate({ status: e.target.value })}
                disabled={statusMutation.isPending}
                className="h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 disabled:opacity-50"
              >
                <option value="ACTIVE">Aktif</option>
                <option value="SUSPENDED">Askıya Alındı</option>
                <option value="INACTIVE">Pasif</option>
              </select>
              {statusMutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-maritime-600" />}
            </div>
          </div>

          {/* Email Doğrulama Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-posta Doğrulama</label>
            <button
              onClick={() => emailVerifyMutation.mutate(!user.emailVerified)}
              disabled={emailVerifyMutation.isPending}
              className={`flex items-center gap-2 h-10 px-4 rounded-maritime text-sm font-medium transition-colors disabled:opacity-50 ${
                user.emailVerified
                  ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
              }`}
            >
              {emailVerifyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {user.emailVerified ? 'Doğrulamayı Kaldır' : 'Doğrula'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
